// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyPicture from "@stoneage/MobiusOverrides/LazyImage/LazyPicture";
import LazyImage from "@stoneage/MobiusOverrides/LazyImage";
import * as React from "react";
import { FC } from "react";

import { css } from "styled-components";

const enum fields {
    largeImagePath = "largeImagePath",
    mediumImagePath = "mediumImagePath",
    smallImagePath = "smallImagePath",
    altText = "altText",
    imageLink = "imageLink",
    width = "width",
    height = "height",
    openInNewTab = "openInNewTab",
    openImageInModal = "openImageInModal",
    horizontalAlignment = "horizontalAlignment",
    disableLazyLoad = "disableLazyLoad",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.largeImagePath]: string;
        [fields.mediumImagePath]: string;
        [fields.smallImagePath]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.openImageInModal]: boolean;
        [fields.width]: string;
        [fields.height]: string;
        [fields.horizontalAlignment]: string;
        [fields.disableLazyLoad]: boolean;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    imageLink?: LinkPresentationProps;
    imageModal?: ModalPresentationProps;
}

export const imageStyles: ImageStyles = {
    wrapper: {
        css: css`
            img {
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    imageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 40px 10px 0;
            `,
            modalContent: css`
                text-align: center;
                padding-bottom: 20px;
            `,
        },
    },
};

const styles = imageStyles;

const Image: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);
    const image = (
        <LazyPicture
            smallImageSrc={fields.smallImagePath}
            mediumImageSrc={fields.mediumImagePath}
            largeImageSrc={fields.largeImagePath}
            altText={fields.altText}
            disableLazyLoad={fields.disableLazyLoad}
            width={fields.width}
            height={fields.height}
        />
    );

    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const openModalHandler = () => {
        setModalIsOpen(true);
    };
    const closeModalHandler = () => {
        setModalIsOpen(false);
    };

    return (
        <StyledWrapper
            css={css`
                ${fields.width && `width:${fields.width}px;`}
                ${fields.height && `height:${fields.height}px;`}
                ${!!fields.horizontalAlignment && `display: flex; justify-content: ${fields.horizontalAlignment}; `}
                && > div:first-child {
                    height: 100%;
                }
                img {
                    max-width: 100%;
                    max-height: ${fields.height ? `${fields.height}px` : "100%"};
                }
            `}
            className="img-wrpr"
        >
            {url ? (
                <Link href={url} target={fields.openInNewTab ? "_blank" : "_self"} {...styles.imageLink}>
                    {image}
                </Link>
            ) : fields.openImageInModal ? (
                <>
                    <Clickable onClick={openModalHandler}>{image}</Clickable>
                    <Modal {...styles.imageModal} isOpen={modalIsOpen} handleClose={closeModalHandler}>
                        <LazyImage
                            src={fields.largeImagePath}
                            height="auto"
                            width="auto"
                            disableLazyLoad={true}
                            altText={fields.altText}
                        />
                    </Modal>
                </>
            ) : (
                image
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Image,
    definition: {
        group: "XNGAGE" as any,
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.largeImagePath,
                displayName: "Large Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },

            {
                name: fields.mediumImagePath,
                displayName: "Medium Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.smallImagePath,
                displayName: "Small Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openInNewTab,
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.openImageInModal,
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.width,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.height,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.horizontalAlignment,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.disableLazyLoad,
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
